import InfiniteScroll from '@cp/src/components/InfiniteScroll/InfiniteScroll';
import LoaderIcon from '@cp/src/assets/icons/LoaderIcon';
import { Typography } from '@navi/web-ui/lib/primitives';
import { useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import { useCallback, useMemo, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { fetchMoreCaseRecommendationCases } from '../caseRecommendationActions';
import { DEFAULT_LIMIT_FOR_RECOMMENDED_CASES } from '../utils';
import cx from 'classnames';
import CaseItem from './CaseItem';

const CaseListContainer = ({
  currentOffset,
  setCurrentOffset
}: {
  currentOffset: number;
  setCurrentOffset: (offset: number) => void;
}) => {
  const dispatch = useDispatch();
  const isLoadingRef = useRef(false);
  const currentOffsetRef = useRef(currentOffset);

  const { caseList } = useSelector((state: RootState) => state.caseRecommendation.recommendedCases);
  const recommendedCasesMetadata = useSelector(
    (state: RootState) => state.caseRecommendation.recommendedMetadata
  );

  useEffect(() => {
    currentOffsetRef.current = currentOffset;
    if (currentOffset > 0) {
      isLoadingRef.current = false;
    }
  }, [currentOffset]);

  const hasMore = useMemo(() => {
    const result =
      currentOffsetRef.current + DEFAULT_LIMIT_FOR_RECOMMENDED_CASES <
      recommendedCasesMetadata.totalCases;
    return result;
  }, [recommendedCasesMetadata.totalCases, caseList.length]);

  const handleLoadMore = useCallback(() => {
    if (isLoadingRef.current || !hasMore) return;

    isLoadingRef.current = true;
    const nextOffset = currentOffsetRef.current + DEFAULT_LIMIT_FOR_RECOMMENDED_CASES;

    setCurrentOffset(nextOffset);

    dispatch(
      fetchMoreCaseRecommendationCases({
        limit: DEFAULT_LIMIT_FOR_RECOMMENDED_CASES,
        offset: nextOffset
      })
    );
  }, [hasMore]);

  return (
    <InfiniteScroll
      dataLength={caseList.length}
      next={handleLoadMore}
      hasMore={hasMore}
      loader={
        <LoaderIcon
          size={20}
          borderWidth={3}
          borderColor="var(--greyscale-light)"
          fillColor="var(--blue-base)"
        />
      }
      endMessage={
        <Typography
          variant="p4"
          color="var(--grayscale-3)"
          className="pb-5 flex items-center justify-center"
        >
          All cases loaded ({caseList.length} of {recommendedCasesMetadata.totalCases})
        </Typography>
      }
      maxHeight="99%"
      scrollThreshold="100px"
    >
      {caseList.map((caseItem, index) => (
        <CaseItem
          key={`${caseItem.caseReferenceId}-${index}`}
          caseItem={caseItem}
          index={index}
          totalItems={caseList.length}
        />
      ))}
    </InfiniteScroll>
  );
};

export default CaseListContainer;
